import { Eye, FileText, Volume2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { VideoThumbnail } from "@/components/video-thumbnail/VideoThumbnail";
import { FILE_ICONS } from "@/features/my-files/assets/file-icons";
import { useImagePreview } from "@/hooks/useImagePreview";

import type { GroupedLogs } from "../../utils/message-parsers";

import { getAttachmentDisplayName } from "../../utils/attachment-display-name";

interface MediaCollectionProps {
  data: GroupedLogs;
  handleOpenPreview: (id: number) => void;
}

// File type classifications
const IMAGE_EXTENSIONS = new Set([
  "jpg",
  "jpeg",
  "png",
  "gif",
  "webp",
  "bmp",
  "svg",
  "tiff",
]);
const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "mov",
  "avi",
  "mkv",
  "webm",
  "m4v",
  "mpeg",
  "mpg",
]);
const AUDIO_EXTENSIONS = new Set(["mp3", "wav", "m4a", "ogg", "mid", "midi"]);
const DOCUMENT_EXTENSIONS = new Set([
  "pdf",
  "doc",
  "docx",
  "txt",
  "ppt",
  "pptx",
  "xls",
  "xlsx",
]);

type MediaType = "audio" | "document" | "image" | "other" | "video";

interface MediaItem {
  attachmentId?: number;
  extension?: string;
  id?: string;
  name?: string;
  type: MediaType;
  url?: string;
}

function getFileExtension(filename: string): string {
  return filename.split(".").pop()?.toLowerCase() || "";
}

function getMediaType(filename: string): MediaType {
  const extension = getFileExtension(filename);

  if (IMAGE_EXTENSIONS.has(extension)) return "image";
  if (VIDEO_EXTENSIONS.has(extension)) return "video";
  if (AUDIO_EXTENSIONS.has(extension)) return "audio";
  if (DOCUMENT_EXTENSIONS.has(extension)) return "document";

  return "other";
}

function ImagePreview({
  item,
  onClick,
}: {
  item: MediaItem;
  onClick: () => void;
}) {
  const { onError, src } = useImagePreview({
    preferredVariant: "thumbnail",
    url: item.url,
  });

  return (
    <div
      className="relative rounded-md overflow-hidden cursor-pointer group"
      onClick={onClick}
      title={item.name}
    >
      <img
        alt={item.name}
        className="aspect-square w-full object-cover"
        loading="lazy"
        onError={onError}
        src={src}
      />
      <div className="absolute rounded-sm inset-0 flex items-center justify-center transition-all duration-300 bg-transparent group-hover:bg-black/50">
        <Eye className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
      </div>
    </div>
  );
}

function MediaPreview({
  item,
  onClick,
}: {
  item: MediaItem;
  onClick: () => void;
}) {
  const IconComponent =
    item.extension && item.extension in FILE_ICONS
      ? FILE_ICONS[item.extension as keyof typeof FILE_ICONS]
      : FILE_ICONS.fallback;

  switch (item.type) {
    case "image": {
      return <ImagePreview item={item} onClick={onClick} />;
    }

    case "video": {
      return (
        <div
          className="relative rounded-md overflow-hidden cursor-pointer group"
          onClick={onClick}
          title={item.name}
        >
          <VideoThumbnail
            attachmentId={item.attachmentId || 0}
            className="aspect-square w-full object-cover"
            extension={item.extension || ""}
            fill
            src={item.url ?? ""}
          />
        </div>
      );
    }

    case "audio": {
      return (
        <div
          className="relative rounded-md overflow-hidden cursor-pointer group bg-gradient-to-br from-blue-500 to-purple-600 aspect-square flex items-center justify-center"
          onClick={onClick}
          title={item.name}
        >
          <div className="text-center">
            <Volume2 className="text-white w-8 h-8 mx-auto mb-2" />
            <p className="text-white text-xs font-medium truncate px-2">
              {item.name}
            </p>
          </div>
          <div className="absolute rounded-sm inset-0 flex items-center justify-center transition-all duration-300 bg-transparent group-hover:bg-black/30">
            <Eye className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
      );
    }

    case "document": {
      return (
        <div
          className="relative rounded-md overflow-hidden cursor-pointer group bg-gray-100 aspect-square flex items-center justify-center"
          onClick={onClick}
          title={item.name}
        >
          <div className="text-center">
            <IconComponent className="w-12 h-12 mx-auto mb-2" />
            <p className="text-gray-700 text-xs font-medium truncate px-2">
              {item.name}
            </p>
          </div>
          <div className="absolute rounded-sm inset-0 flex items-center justify-center transition-all duration-300 bg-transparent group-hover:bg-black/30">
            <Eye className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
      );
    }

    default: {
      return (
        <div
          className="relative rounded-md overflow-hidden cursor-pointer group bg-gray-50 aspect-square flex items-center justify-center"
          onClick={onClick}
          title={item.name}
        >
          <div className="text-center">
            <IconComponent className="w-12 h-12 mx-auto mb-2" />
            <p className="text-gray-600 text-xs font-medium truncate px-2">
              {item.name}
            </p>
          </div>
          <div className="absolute rounded-sm inset-0 flex items-center justify-center transition-all duration-300 bg-transparent group-hover:bg-black/30">
            <FileText className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          </div>
        </div>
      );
    }
  }
}

export function MediaCollection({
  data,
  handleOpenPreview,
}: MediaCollectionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const hasMainComment = data.items.find(
    (item) => item.asanaResourceType === "story",
  );

  const { hasMore, mediaItems, visibleCount } = useMemo(() => {
    const items: MediaItem[] = data.items
      .filter((item) => item.asanaResourceType === "attachment")
      .map((item) => {
        const extension = getFileExtension(
          item.attachment?.path ||
            item.attachment?.name ||
            item?.name ||
            item.message ||
            "",
        );

        return {
          attachmentId: item.attachmentId,
          extension,
          id: item.id,
          name: getAttachmentDisplayName(
            item.attachment,
            item.message || item.name,
          ),
          type: extension ? getMediaType(extension) : "other",
          url: item.url,
        };
      });

    const visible = isExpanded ? items.length : Math.min(4, items.length);
    const more = items.length > Math.min(4, items.length);

    return {
      hasMore: more,
      mediaItems: items,
      visibleCount: visible,
    };
  }, [data.items, isExpanded]);

  const visibleItems = useMemo(
    () => mediaItems.slice(0, visibleCount),
    [mediaItems, visibleCount],
  );

  const toggleExpand = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleItemClick = useCallback(
    (item: MediaItem, index: number) => {
      if (index === visibleCount - 1 && !isExpanded && hasMore) {
        toggleExpand();
      } else if (item.url && item.attachmentId) {
        handleOpenPreview(item.attachmentId);
      }
    },
    [hasMore, isExpanded, toggleExpand, visibleCount],
  );

  const gridCols = useMemo(() => {
    const count = visibleItems.length;

    if (count === 1) return "grid-cols-1";
    if (count === 2) return "grid-cols-2";

    return "grid-cols-2";
  }, [visibleItems.length]);

  const shouldShowButton = mediaItems.length > 4;

  return (
    <>
      <div className="flex flex-col gap-2 w-full">
        {hasMainComment && (
          <div className="mb-4 mt-2">
            <p
              className="break-words"
              dangerouslySetInnerHTML={{ __html: hasMainComment.message }}
            />
          </div>
        )}
        <div className={`grid ${gridCols} gap-2`}>
          {visibleItems.map((item, index) => (
            <div key={`${item.id}-${index}`}>
              <MediaPreview
                item={item}
                onClick={() => handleItemClick(item, index)}
              />
            </div>
          ))}
        </div>

        {shouldShowButton && (
          <div className="w-full flex justify-center">
            <button
              className="text-utility-brand-700 font-semibold flex items-center gap-1 text-sm my-2 cursor-pointer"
              onClick={toggleExpand}
              type="button"
            >
              {isExpanded ? "Show less" : `Show ${mediaItems.length - 4} more`}
              <svg
                className={`inline-block transition-transform ${isExpanded ? "rotate-180" : ""}`}
                fill="none"
                height="20"
                stroke="currentColor"
                viewBox="0 0 24 24"
                width="20"
              >
                <path
                  d="M19 9l-7 7-7-7"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </button>
          </div>
        )}
      </div>
    </>
  );
}
